import {Tooltip} from 'antd';
import styles from './dashboard.module.scss';

/**
 * The primitives every dashboard tab is built from.
 *
 * Each of the six tabs previously invented its own card, its own section
 * heading level and its own empty state — one tab used `<h2>` for a section
 * another gave `<h4>`, and "no data" was sometimes a styled block and sometimes
 * a bare `<p>`. Sharing them is what makes the six read as one dashboard.
 */

const InfoIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 7.6h.01" />
  </svg>
);

/**
 * One figure.
 *
 * `tone` is reserved for meaning (a delta that is good or bad), never for
 * decoration — colour on a number is a claim about it.
 */
export const StatCard = ({
  label,
  value,
  hint,
  unit,
  tone,
  info,
  loading,
  onClick,
}) => {
  const Element = onClick ? 'button' : 'div';
  return (
    <Element
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`${styles.stat} ${onClick ? styles.statAction : ''} ${
        tone ? styles[`stat${tone}`] : ''
      }`}>
      <span className={styles.statLabel}>
        {label}
        {info && (
          <Tooltip title={info}>
            <span className={styles.statInfo}>
              <InfoIcon />
            </span>
          </Tooltip>
        )}
      </span>
      {loading ? (
        <span className={styles.statSkeleton} />
      ) : (
        <span className={styles.statValue}>
          {unit && <span className={styles.statUnit}>{unit}</span>}
          {value}
        </span>
      )}
      {hint && <span className={styles.statHint}>{hint}</span>}
    </Element>
  );
};

export const StatRow = ({children, columns}) => (
  <div
    className={styles.statRow}
    style={columns ? {'--stat-columns': columns} : undefined}>
    {children}
  </div>
);

/** A titled block. `meta` carries the scope the block is true for. */
export const Section = ({title, description, meta, actions, children}) => (
  <section className={styles.section}>
    {(title || actions) && (
      <header className={styles.sectionHead}>
        <div>
          {title && <h3>{title}</h3>}
          {description && <p>{description}</p>}
        </div>
        {meta && <span className={styles.sectionMeta}>{meta}</span>}
        {actions}
      </header>
    )}
    {children}
  </section>
);

export const Card = ({children, className = '', padded = true}) => (
  <div
    className={`${styles.card} ${
      padded ? styles.cardPadded : ''
    } ${className}`}>
    {children}
  </div>
);

export const EmptyState = ({title, description, compact}) => (
  <div className={`${styles.empty} ${compact ? styles.emptyCompact : ''}`}>
    <svg
      width="34"
      height="34"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <path d="M4 19V5" />
      <path d="M4 19h16" />
      <path d="M8 15.5v-3M12.5 15.5v-6M17 15.5v-4" />
    </svg>
    <h4>{title}</h4>
    {description && <p>{description}</p>}
  </div>
);

/**
 * A chart viewport.
 *
 * `minContentWidth` is a floor, not a width: the canvas fills the frame when it
 * fits and overflows into a horizontal scroll when it does not. Expressing it
 * that way means the frame never has to be measured — a fixed pixel width would
 * have to be computed against a container that has no size on first paint.
 *
 * `scrollRef` lets the caller park the view at the latest data rather than the
 * oldest, which is where a reader starts with a time series.
 */
export const ChartFrame = ({
  children,
  height = 320,
  minContentWidth,
  scrollRef,
}) => {
  if (!minContentWidth)
    return (
      <div className={styles.chartFrame} style={{height}}>
        {children}
      </div>
    );

  return (
    <div
      ref={scrollRef}
      className={`${styles.chartFrame} ${styles.chartScroll}`}
      style={{height}}>
      <div style={{minWidth: minContentWidth, width: '100%', height: '100%'}}>
        {children}
      </div>
    </div>
  );
};

/**
 * A legend that carries the values.
 *
 * Chart.js's own legend only shows which colour is which, so a doughnut left
 * the reader guessing at every segment. Naming the number beside the swatch is
 * what turns the shape into data.
 */
export const ValueLegend = ({items, total}) => (
  <ul className={styles.legend}>
    {items.map(item => (
      <li key={item.label}>
        <span
          className={styles.legendSwatch}
          style={{background: item.color}}
        />
        <span className={styles.legendLabel}>{item.label}</span>
        <span className={styles.legendValue}>{item.display ?? item.value}</span>
        {total > 0 && (
          <span className={styles.legendShare}>
            {Math.round((item.value * 100) / total)}%
          </span>
        )}
      </li>
    ))}
  </ul>
);

/** A horizontal share bar — used wherever parts of a whole are listed. */
export const ShareBar = ({value, total, color}) => (
  <div className={styles.shareTrack}>
    <div
      className={styles.shareFill}
      style={{
        width: `${total > 0 ? Math.min(100, (value * 100) / total) : 0}%`,
        background: color,
      }}
    />
  </div>
);
